import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaGoogle, FaGithub, FaTwitter, FaEnvelope } from 'react-icons/fa';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { emailAuthApi } from '../utils/apiUtils';
import { useNavigate, Link } from 'react-router-dom';

function SignIn() {
  const { signIn } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [timerActive, setTimerActive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let timer;
    if (timerActive && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
    } else if (timeLeft === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(timer);
  }, [timerActive, timeLeft]);

  const handleGoogleSignIn = () => {
    signIn('google').catch(error => {
      navigate('/error', { state: { error: { status: error.status || 500, message: error.message || 'Failed to sign in with Google', details: 'Please try again later or contact support.' } } });
    });
  };

  const handleGithubSignIn = () => {
    signIn('github').catch(error => {
      navigate('/error', { state: { error: { status: error.status || 500, message: error.message || 'Failed to sign in with GitHub', details: 'Please try again later or contact support.' } } });
    });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    try {
      await emailAuthApi.requestEmailAuth(email);
      setShowCodeInput(true);
      setTimeLeft(60);
      setTimerActive(true);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to send authentication code.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    try {
      await emailAuthApi.verifyEmailAuth(email, code);
      window.location.href = '/user';
    } catch (error) {
      setErrorMessage(error.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      await emailAuthApi.requestEmailAuth(email);
      setTimeLeft(60);
      setTimerActive(true);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}>
      <div className="flex items-center justify-center min-h-[70vh] py-8">
        <motion.div
          className="w-full max-w-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="bg-white rounded-2xl shadow-card-hover overflow-hidden border border-gray-100">
            {/* Accent bar */}
            <div className="h-1 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-red-400" />

            <div className="p-7 sm:p-9">
              <div className="text-center mb-7">
                <span className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-semibold">// auth</span>
                <h2 className="text-xl font-bold text-gray-900 mt-2">Welcome Back</h2>
              </div>

              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 rounded-lg bg-red-50 p-3 text-xs text-red-600 border border-red-100"
                >
                  {errorMessage}
                </motion.div>
              )}

              <div className="space-y-2.5">
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGoogleSignIn}
                  className="w-full flex items-center px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors text-xs font-medium"
                >
                  <FaGoogle className="w-3.5 h-3.5 mr-3 text-gray-400" />
                  Continue with Google
                </motion.button>

                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGithubSignIn}
                  className="w-full flex items-center px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors text-xs font-medium"
                >
                  <FaGithub className="w-3.5 h-3.5 mr-3 text-gray-400" />
                  Continue with GitHub
                </motion.button>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
                  <div className="relative flex justify-center text-[10px]">
                    <span className="px-3 bg-white text-gray-300 uppercase tracking-widest">or</span>
                  </div>
                </div>

                <form onSubmit={showCodeInput ? handleCodeSubmit : handleEmailSubmit} className="space-y-2.5">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@university.edu"
                    disabled={showCodeInput}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-xs focus:ring-1 focus:ring-purple-500/20 focus:border-purple-300 transition-all disabled:bg-gray-50 disabled:text-gray-400"
                    required
                  />

                  {showCodeInput && (
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => setCode(e.target.value)}
                          placeholder="Verification code"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-xs focus:ring-1 focus:ring-purple-500/20 focus:border-purple-300 transition-all font-code tracking-widest"
                          disabled={timeLeft === 0}
                          required
                        />
                        {timeLeft > 0 && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-300 font-code tabular-nums">
                            {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                          </span>
                        )}
                      </div>
                      {timeLeft === 0 && (
                        <button type="button" onClick={handleResendCode} disabled={loading}
                          className="w-full px-4 py-2 rounded-lg bg-gray-50 text-gray-500 text-xs font-medium hover:bg-gray-100 transition-colors"
                        >
                          {loading ? 'Sending...' : 'Resend Code'}
                        </button>
                      )}
                    </div>
                  )}

                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center px-4 py-2.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 focus:outline-none transition-all text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                  >
                    <FaEnvelope className="w-3 h-3 mr-2" />
                    {loading ? 'Loading...' : showCodeInput ? 'Verify Code' : 'Email Sign In'}
                  </motion.button>
                </form>

                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
                  <div className="relative flex justify-center text-[10px]">
                    <span className="px-3 bg-white text-gray-300">Coming Soon</span>
                  </div>
                </div>

                <button disabled className="w-full flex items-center px-4 py-2.5 rounded-lg border border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed text-xs font-medium">
                  <FaTwitter className="w-3.5 h-3.5 mr-3" />
                  Continue with Twitter
                </button>
              </div>

              <p className="mt-7 text-center text-[10px] text-gray-300">
                By signing in you agree to our{' '}
                <Link to="/terms-of-service" className="text-purple-400 hover:text-purple-600">Terms</Link>
                {' & '}
                <Link to="/privacy-policy" className="text-purple-400 hover:text-purple-600">Privacy</Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </GoogleOAuthProvider>
  );
}

export default SignIn;
